// @ts-nocheck
// Draft state + per-section save pipeline for the registration edit page.
//
// The page edits one Registration through independent section saves. Because
// the API update is a `$set` of the sent body and `config` is a subdocument
// (sending it replaces the whole object), every save must include a FULL
// config. To keep section saves independent (saving Schedule must not silently
// persist unsaved Roster edits), each config key has an owning section: the
// merged config uses the DRAFT value for keys owned by the section being
// saved and the LAST-SAVED value for everything else.
//
// The Roster section additionally mirrors its keys onto the parent
// tournament/league config (ported from the retired wizard's
// FirstStep/LeagueFirstStep). That dual-write is load-bearing: config
// resolution precedence is division > league/tournament > registration
// (packages/shared/utils/getConfig.ts), so the parent copy is what divisions
// actually inherit.
import { useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { toast } from 'sonner';
import { useAuth } from '@/auth/clerkShim';
import useRegistrationStore from '@store/registrationStore';
import useTournamentStore from '@store/tournamentStore';
import useLeagueStore from '@store/leagueStore';
import helpers from '@/utils/helpers';
import ensureEmailField from '@/utils/ensureEmailField';
import resolveExcludeTeamNameSeed, { sportDefaultExcludeTeamName } from '@/utils/resolveExcludeTeamNameSeed';
import { browserTimeZone, toDatetimeLocalInZone, fromDatetimeLocalInZone } from '@/utils/registrationDateTz';
import { CONFIG_DEFAULTS } from '@staty/shared/utils/configDefaults';
import { stripQuillNbsp } from '@/utils/quillHtml';

export const SECTION_IDS = [
  'overview', 'schedule', 'payments', 'roster', 'legal', 'messaging', 'formFields',
];

// Draft keys per section (dirty tracking + savedDraft merge on success).
// `schedule` is the "Registration Access" pane (open/close gates + access code);
// `formFields` combines the player and team form-field lists into one pane.
const SECTION_KEYS = {
  overview: ['active', 'showAllRegistrationTeams'],
  schedule: ['gateByStartDate', 'registrationStartDate', 'gateByEndDate', 'registrationEndDate', 'registrationTimeZone', 'accessCode'],
  payments: ['collectPayments', 'selectedPaymentProduct', 'collectDonations', 'selectedDonationProduct'],
  roster: [
    'allowCoaches', 'minCoaches', 'maxCoaches',
    'requirePlayers', 'minPlayers', 'maxPlayers', 'playerAutoComplete',
    'requireTeamRepresentatives', 'minTeamRepresentatives', 'maxTeamRepresentatives',
    'allowFreeAgents',
  ],
  legal: ['releaseStatement', 'selectedWaivers'],
  messaging: ['message', 'thankYouMessage'],
  // excludeTeamName moved here from roster (owner 2026-09-29) — the "Require
  // team name" toggle lives on the Team form tab, next to its live preview.
  formFields: ['fields', 'teamFields', 'excludeTeamName'],
};

// Which section owns each config key (null = preserved from the loaded doc).
const CONFIG_KEY_OWNER = {
  requirePlayers: 'roster',
  requireCoaches: 'roster',
  requireTeamRepresentatives: 'roster',
  // excludeTeamName is deliberately NOT here — it has tri-state semantics
  // (explicit boolean at any tier wins, ABSENT means "sport default", ADR
  // 0049) that the generic always-write loop would destroy by stamping the
  // resolved draft value on every section save. buildConfig special-cases it
  // for the formFields section only.
  minCoaches: 'roster',
  maxCoaches: 'roster',
  minPlayers: 'roster',
  maxPlayers: 'roster',
  minTeamRepresentatives: 'roster',
  maxTeamRepresentatives: 'roster',
  playerAutoComplete: 'roster',
  showAllRegistrationTeams: 'overview',
  gateByStartDate: 'schedule',
  gateByEndDate: 'schedule',
  // Registration-OWN key (ADR 0054, like showAllRegistrationTeams): flows into
  // registration.config on roster saves but is deliberately NOT in
  // PARENT_CONFIG_KEYS — it must never be mirrored onto the parent
  // tournament/league config (the public funnel reads it off
  // registration.config directly, never via getConfig).
  allowFreeAgents: 'roster',
};

const hasContent = (html) => !!html && html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length > 0;

// Same value rules the wizard applied when assembling config.
function configValue(key, d) {
  switch (key) {
    case 'requireCoaches': return d.allowCoaches;
    case 'minCoaches': return d.allowCoaches ? Number(d.minCoaches) : undefined;
    case 'maxCoaches': return d.allowCoaches ? Number(d.maxCoaches) : undefined;
    case 'minPlayers': return d.requirePlayers ? Number(d.minPlayers) : undefined;
    case 'maxPlayers': return d.requirePlayers ? Number(d.maxPlayers) : undefined;
    case 'minTeamRepresentatives': return d.requireTeamRepresentatives ? Number(d.minTeamRepresentatives) : undefined;
    case 'maxTeamRepresentatives': return d.requireTeamRepresentatives ? Number(d.maxTeamRepresentatives) : undefined;
    case 'playerAutoComplete': return d.requirePlayers ? d.playerAutoComplete : false;
    default: return d[key];
  }
}

// The 9 roster keys mirrored onto the parent tournament/league config.
// excludeTeamName mirrors too, but through the formFields save's explicit
// patch/omit path (tri-state, ADR 0049) — never this always-write list.
const PARENT_CONFIG_KEYS = [
  'requirePlayers', 'requireCoaches', 'requireTeamRepresentatives',
  'minCoaches', 'maxCoaches', 'minPlayers', 'maxPlayers',
  'minTeamRepresentatives', 'maxTeamRepresentatives',
];

// The registration's sport (ADR 0010 mirror). The page-level fallback to
// organization.sports[0] is deliberately NOT replicated here — the hook only
// sees the registration; registrations created through the admin carry
// `sports`, and a legacy doc missing it degrades to the team-sport default
// (explicit toggling still works).
function registrationSport(r) {
  return _.get(r, 'sports.0', _.get(r, 'sport', '')) || '';
}

function draftFromRegistration(r) {
  const tz = _.get(r, 'timeZone') || browserTimeZone();
  return {
    // overview
    active: !!_.get(r, 'active', false),
    showAllRegistrationTeams: !!_.get(r, 'config.showAllRegistrationTeams', false),
    // schedule
    gateByStartDate: !!_.get(r, 'config.gateByStartDate', false),
    registrationStartDate: toDatetimeLocalInZone(_.get(r, 'startDate'), tz),
    gateByEndDate: !!_.get(r, 'config.gateByEndDate', false),
    registrationEndDate: toDatetimeLocalInZone(_.get(r, 'endDate'), tz),
    registrationTimeZone: tz,
    // payments
    collectPayments: !!_.get(r, 'paymentProduct'),
    selectedPaymentProduct: helpers.getObjId(_.get(r, 'paymentProduct', null)),
    collectDonations: !!_.get(r, 'donationProduct'),
    selectedDonationProduct: helpers.getObjId(_.get(r, 'donationProduct', null)),
    // roster
    allowCoaches: _.get(r, 'config.allowCoaches', _.get(r, 'allowCoaches', CONFIG_DEFAULTS.requireCoaches)),
    minCoaches: String(_.get(r, 'config.minCoaches', CONFIG_DEFAULTS.minCoaches)),
    maxCoaches: String(_.get(r, 'config.maxCoaches', CONFIG_DEFAULTS.maxCoaches)),
    requirePlayers: _.get(r, 'config.requirePlayers', CONFIG_DEFAULTS.requirePlayers),
    minPlayers: String(_.get(r, 'config.minPlayers', '1')), // FORM SEED, not a platform default (ADR 0035)
    maxPlayers: String(_.get(r, 'config.maxPlayers', '15')), // FORM SEED, not a platform default (ADR 0035 — platform default is unlimited)
    playerAutoComplete: !!_.get(r, 'config.playerAutoComplete', false),
    requireTeamRepresentatives: _.get(r, 'config.requireTeamRepresentatives', CONFIG_DEFAULTS.requireTeamRepresentatives),
    // Only used when the requireTeamRepresentatives toggle is turned ON (see
    // configValue) — a 1/2 suggestion at enable time, deliberately NOT the
    // platform 0/5. FORM SEED, not a platform default (ADR 0035).
    minTeamRepresentatives: String(_.get(r, 'config.minTeamRepresentatives', '1')),
    maxTeamRepresentatives: String(_.get(r, 'config.maxTeamRepresentatives', '2')),
    // RESOLVED seed, not the raw stored value (which is tri-state): an unset
    // value used to display as OFF here, and the old roster save then stamped
    // an explicit `false` that beat the ADR 0049 individual-sport default —
    // re-imposing a required Team Name on pickleball registrations. The
    // parent tier arrives async and is reseeded by the effect in the hook.
    excludeTeamName: resolveExcludeTeamNameSeed({
      registrationConfig: _.get(r, 'config'),
      sport: registrationSport(r),
    }),
    allowFreeAgents: !!_.get(r, 'config.allowFreeAgents', false),
    // legal
    releaseStatement: _.get(r, 'releaseStatement', ''),
    selectedWaivers: _.get(r, 'playerWaivers', []).map((pw) => helpers.getObjId(pw.waiver)),
    // messaging & access
    message: _.get(r, 'message', ''),
    thankYouMessage: _.get(r, 'thankYouMessage', ''),
    accessCode: _.get(r, 'accessCode', ''),
    // form fields
    fields: _.get(r, 'fields', []),
    teamFields: _.get(r, 'teamFields', []),
  };
}

export default function useRegistrationDraft(registrationId) {
  const { getToken } = useAuth();
  const [registration, setRegistration] = useState(null);
  const [draft, setDraft] = useState(null);
  const [savedDraft, setSavedDraft] = useState(null);
  const [savingSection, setSavingSection] = useState(null);
  // The registration's FULL config as of load/last save. Config is a $set-replaced
  // subdocument and carries many keys this page doesn't own (tiebreakers,
  // eligibility, capacity/waitlist, pools, …) — every save starts from this
  // object so those keys are never wiped. (The retired wizard had that bug.)
  const savedConfigRef = useRef({});

  const { getRegistrationById, updateRegistration } = useRegistrationStore((state) => ({
    getRegistrationById: state.getRegistrationById,
    updateRegistration: state.updateRegistration(getToken),
  }));
  const { updateTournament, getTournamentById } = useTournamentStore((state) => ({
    updateTournament: state.updateTournament(getToken),
    getTournamentById: state.getTournamentById,
  }));
  const tournaments = useTournamentStore((state) => state.tournaments);
  const { updateSeason, getLeagueById } = useLeagueStore((state) => ({
    updateSeason: state.updateLeague(getToken),
    getLeagueById: state.getLeagueById,
  }));
  const seasons = useLeagueStore((state) => state.leagues);

  useEffect(() => {
    if (!registrationId) return;
    getRegistrationById(registrationId, (r) => {
      setRegistration(r);
      savedConfigRef.current = _.cloneDeep(r.config || {});
      const d = draftFromRegistration(r);
      setDraft(d);
      setSavedDraft(d);
      // Load the parent doc so the roster dual-write has a target.
      const tournamentId = helpers.getObjId(r.tournament);
      const contestId = helpers.getObjId(r.contest);
      if (r.contestType === 'tournament' && tournamentId) getTournamentById(tournamentId);
      if (r.contestType !== 'tournament' && contestId) getLeagueById(contestId, () => {});
    });
  }, [registrationId]);

  const contestType = _.get(registration, 'contestType', 'tournament');
  const parentTournament = useMemo(
    () => _.find(tournaments, { _id: helpers.getObjId(registration?.tournament) }),
    [tournaments, registration],
  );
  const parentSeason = useMemo(
    () => _.find(seasons, { _id: helpers.getObjId(registration?.contest) }),
    [seasons, registration],
  );

  // Sport for the excludeTeamName tri-state: the registration's own mirror
  // first (ADR 0010), else the PARENT's — legacy registrations can carry
  // `sports: []` while the parent league/tournament has the real value, and
  // the public resolver reaches it the same way. Empty until the parent loads;
  // the reseed effect below corrects the initial seed once it does.
  const parentEntity = contestType === 'tournament' ? parentTournament : parentSeason;
  const sport = registration
    ? (registrationSport(registration) || _.get(parentEntity, 'sports.0', ''))
    : '';

  // Parent-arrival reseed for excludeTeamName: the parent tournament/league is
  // the authoritative tier (ADR 0034) but loads async, so the initial seed
  // could only consider registration config + the registration's own sport.
  // Once the parent is available, re-run the FULL seed (parent explicit value
  // AND parent-derived sport default) — but only when the registration has no
  // explicit value of its own and the admin hasn't touched the toggle.
  // Deliberately IDEMPOTENT (no one-shot ref): the load effect runs twice
  // under StrictMode and its second callback re-seeds the raw value AFTER a
  // one-shot reseed already fired; running on every relevant change converges
  // instead. Stable post-save too: the parent store copy carries the mirrored
  // value, so the recomputed seed equals the saved draft.
  useEffect(() => {
    if (!registration || !draft || !savedDraft || !parentEntity) return;
    if (typeof _.get(registration, 'config.excludeTeamName') === 'boolean') return;
    if (draft.excludeTeamName !== savedDraft.excludeTeamName) return; // admin mid-toggle
    const reseeded = resolveExcludeTeamNameSeed({
      registrationConfig: _.get(registration, 'config'),
      parentConfig: _.get(parentEntity, 'config'),
      sport: registrationSport(registration) || _.get(parentEntity, 'sports.0', ''),
    });
    if (reseeded === draft.excludeTeamName) return;
    setDraft((prev) => ({ ...prev, excludeTeamName: reseeded }));
    setSavedDraft((prev) => ({ ...prev, excludeTeamName: reseeded }));
  }, [registration, draft, savedDraft, parentEntity]);

  const setField = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));

  // Revert one section's draft slice to its last-saved values (the "Discard
  // changes" path of the unsaved-changes interception when switching panes).
  const resetSection = (section) => {
    setDraft((prev) => ({ ...prev, ..._.pick(savedDraft, SECTION_KEYS[section]) }));
  };

  const dirtyBySection = useMemo(() => {
    const out = {};
    if (!draft || !savedDraft) return out;
    for (const section of SECTION_IDS) {
      out[section] = !_.isEqual(_.pick(draft, SECTION_KEYS[section]), _.pick(savedDraft, SECTION_KEYS[section]));
    }
    return out;
  }, [draft, savedDraft]);

  // Full config: starts from the complete last-saved config (preserving keys
  // this page doesn't own), then overrides the owned keys — draft values for
  // keys owned by `section`, last-saved values for every other owned key.
  const buildConfig = (section) => {
    const config = { ...savedConfigRef.current };
    for (const [key, owner] of Object.entries(CONFIG_KEY_OWNER)) {
      config[key] = configValue(key, owner === section ? draft : savedDraft);
    }
    // excludeTeamName no-stamping (ADR 0049): only a Form Fields save touches
    // it. An explicit boolean is persisted ONLY when it differs from the
    // sport default — otherwise the key is OMITTED, which (config being
    // $set-replaced wholesale) also clears a stale explicit value and
    // restores resolution-time defaulting. Other sections preserve whatever
    // is stored, exactly like un-owned keys.
    if (section === 'formFields') {
      if (draft.excludeTeamName === sportDefaultExcludeTeamName(sport)) {
        delete config.excludeTeamName;
      } else {
        config.excludeTeamName = !!draft.excludeTeamName;
      }
    }
    return config;
  };

  const buildSectionPayload = (section) => {
    const d = draft;
    switch (section) {
      case 'overview':
        return { active: !!d.active };
      case 'schedule':
        return {
          timeZone: d.registrationTimeZone,
          accessCode: (d.accessCode || '').trim(),
          // Only write dates when their gate is on AND a date was picked, so we
          // never clobber an existing (eligibility-fallback) start/end date.
          ...(d.gateByStartDate && d.registrationStartDate
            ? { startDate: fromDatetimeLocalInZone(d.registrationStartDate, d.registrationTimeZone) }
            : {}),
          ...(d.gateByEndDate && d.registrationEndDate
            ? { endDate: fromDatetimeLocalInZone(d.registrationEndDate, d.registrationTimeZone) }
            : {}),
        };
      case 'payments':
        // `collectPayments` / `collectDonations` are DRAFT-ONLY UI state and are
        // deliberately not sent: they are not on RegistrationSchema, so
        // sanitizeModelPayload strips them client-side and Mongoose strict mode
        // would drop them anyway. The product ref IS the persisted source of
        // truth — the toggles are re-derived from it on load (see buildDraft).
        //
        // The ref must be written on BOTH branches. Previously the key was
        // omitted when a toggle was off, so `$set` never cleared it: the fee
        // stayed attached, the public site kept charging, and the switch sprang
        // back ON at the next load — with a success toast. See ADM-09-34.
        //
        // `|| null` matters: getObjId passes '' through unchanged, and '' is not
        // castable to an ObjectId (CastError -> 500 -> "Update Error" toast).
        return {
          paymentProduct: d.collectPayments ? (d.selectedPaymentProduct || null) : null,
          donationProduct: d.collectDonations ? (d.selectedDonationProduct || null) : null,
        };
      case 'roster':
        return { allowCoaches: d.allowCoaches };
      case 'legal': {
        const hasRelease = hasContent(d.releaseStatement);
        return {
          requireReleaseStatement: hasRelease,
          // Quill 2 glues words with &nbsp;; saved as-is the statement renders
          // as one unbreakable line on the public release-statement step
          // (ocsabasketball, 2026-10-16). Same strip WaiverAdmin applies.
          releaseStatement: hasRelease ? stripQuillNbsp(d.releaseStatement) : '',
          playerWaivers: (d.selectedWaivers || []).map((waiverId) => ({ required: true, waiver: waiverId })),
        };
      }
      case 'messaging': {
        const hasThanks = hasContent(d.thankYouMessage);
        return {
          message: hasContent(d.message) ? d.message : '',
          requireThankYouMessage: hasThanks,
          thankYouMessage: hasThanks ? d.thankYouMessage : '',
        };
      }
      case 'formFields':
        // One form, one save: both lists persist together.
        return { fields: ensureEmailField(d.fields, registration), teamFields: d.teamFields };
      default:
        return {};
    }
  };

  // Keys mirrored onto the parent tournament/league config. With ADR 0034 the
  // parent is the AUTHORITATIVE home (the registration is not a resolution
  // tier) — this mirror is what makes these settings effective. `patchOverride`
  // lets the payments/formFields sections mirror their own keys; `omitKeys`
  // DELETES keys on the parent (its config is $set-replaced wholesale, so a
  // key missing from the sent object is gone) — the formFields save uses it
  // to clear a stale explicit excludeTeamName when the toggle sits at the
  // sport default.
  const saveParentConfig = (patchOverride, omitKeys = []) => new Promise((resolve) => {
    const patch = patchOverride || {};
    if (!patchOverride) {
      for (const key of PARENT_CONFIG_KEYS) patch[key] = configValue(key, draft);
    }
    const mergedConfig = (base) => _.omit({ ...(base || {}), ...patch }, omitKeys);
    if (contestType === 'tournament') {
      if (!parentTournament) { resolve(false); return; }
      updateTournament(
        { ...parentTournament, config: mergedConfig(parentTournament.config) },
        () => resolve(true),
      );
    } else {
      if (!parentSeason) { resolve(false); return; }
      updateSeason(
        { ...parentSeason, config: mergedConfig(parentSeason.config) },
        () => resolve(true),
      );
    }
  });

  const saveSection = async (section) => {
    if (!draft || savingSection) return;
    setSavingSection(section);
    try {
      const config = buildConfig(section);
      const payload = { _id: registrationId, ...buildSectionPayload(section), config };
      await new Promise((resolve, reject) => {
        try { updateRegistration(payload, resolve); } catch (e) { reject(e); }
      });
      savedConfigRef.current = config;
      if (section === 'roster' || section === 'payments' || section === 'formFields') {
        // Payments mirror the fee/donation refs into the parent's config —
        // that is where pricing resolution reads them (ADR 0034). The
        // registration's own copy is kept in sync only to seed this form.
        // formFields mirrors excludeTeamName the same way, with the ADR 0049
        // tri-state: explicit only when it differs from the sport default,
        // otherwise omitted on BOTH tiers (which clears a stale explicit).
        const wantExplicitExclude = draft.excludeTeamName !== sportDefaultExcludeTeamName(sport);
        const synced = await saveParentConfig(
          section === 'payments' ? {
            paymentProduct: draft.collectPayments ? (draft.selectedPaymentProduct || null) : null,
            donationProduct: draft.collectDonations ? (draft.selectedDonationProduct || null) : null,
          } : section === 'formFields'
            ? (wantExplicitExclude ? { excludeTeamName: !!draft.excludeTeamName } : {})
            : undefined,
          section === 'formFields' && !wantExplicitExclude ? ['excludeTeamName'] : [],
        );
        if (!synced) {
          toast.warning('Saved, but the tournament/league copy of these settings could not be synced. Reopen this page and save again.');
        }
      }
      setSavedDraft((prev) => ({ ...prev, ..._.pick(draft, SECTION_KEYS[section]) }));
    } finally {
      setSavingSection(null);
    }
  };

  // Warn on tab close/refresh with unsaved edits.
  const anyDirty = Object.values(dirtyBySection).some(Boolean);
  useEffect(() => {
    if (!anyDirty) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [anyDirty]);

  return {
    registration,
    contestType,
    // The excludeTeamName-resolution sport (registration mirror, else parent) —
    // exposed so the Form Fields UI's copy/preview agree with the save
    // pipeline's tri-state baseline.
    sport,
    draft,
    setField,
    resetSection,
    dirtyBySection,
    anyDirty,
    savingSection,
    saveSection,
    parentName: contestType === 'tournament'
      ? _.get(parentTournament, 'name', _.get(registration, 'tournament.name', ''))
      : _.get(parentSeason, 'name', _.get(registration, 'contest.name', _.get(registration, 'seasonName', ''))),
    parentSynced: contestType === 'tournament' ? !!parentTournament : !!parentSeason,
  };
}
